import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { calculateLevelBasedPoints, POINTS } from '../utils/levelCalculation';
import { formatDateToLocal } from '../utils/date';
import { addDays, format, differenceInCalendarDays, parseISO } from 'date-fns';
import {
  calculateModuleXPFromModule,
  calculateProgramTotalXP,
  calculateCompletionStatus,
  mergeXPDistributions,
  calculateScaledModuleXP,
  calculateScaledCompletionBonus,
  calculateDurationBonus,
  getDifficultyMultiplier
} from '../utils/programXP';
import type {
  Program,
  ProgramModule,
  ProgramEnrollment,
  ProgramDifficulty,
  ProgramStatus,
  ScheduleSlot,
  ProgramInitiation,
  ProgramFolder,
  InitiationDayStatusRecord,
  XPDistribution,
  ProgramCompletionReport,
  HabitXPRecord
} from '../types';
import { useActivityLogStore } from './activityLogStore';
import { useFitnessStore } from './fitnessStore';
import { useHabitStore } from './habitStore';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { dedupeInit, isCloudFresh, markCloudFresh } from '../lib/cloudBoot';
import {
  accountGeneration, accountStorage, cachedUserIdOwner, scopeToAccount, swappingAccount,
} from '../lib/accountStorage';
import {
  createBlobSync, mergeCollection, mergeTombstones, pruneTombstones, tombstone, sameRecords,
  matchesCloud, sameTombstones,
  type Tombstones,
} from '../lib/syncBlob';
import { supabaseBlobTransport } from '../lib/syncBlobTransport';

// ─── Supabase sync (sync epic) ──────────────────────────────────────────────
// Programs + enrollments + initiations sync as ONE atomic blob per user
// (user_programs.data). Whole-store-blob keeps the nested data consistent. A
// single store subscription auto-syncs on any of the four collections changing.

const USER_PROGRAMS_TABLE = 'user_programs';
function readTombstones(data: any): Tombstones {
  const raw = data?.deleted;
  return raw && typeof raw === 'object' ? (raw as Tombstones) : {};
}

/** Initiations saved before per-module tracking have no completedModuleIds. */
const backfillInitiations = (list: any[]) =>
  list.map((i) => (i.completedModuleIds ? i : { ...i, completedModuleIds: [] }));

/**
 * ⚠️⚠️ **A PAUSE FREEZES THE RUN'S CLOCK** (owner, 2026-10-16). Pause used to keep
 * every date, so Resume after a long pause left the remaining days in the past and
 * `checkAndMarkMissedDays` skipped them all on the next open — the run was dead on
 * resume. Now every unfinished day (pending or rest) dated on or after the pause
 * day moves on by the days spent paused, so the run picks up where it stopped with
 * its rhythm intact. Days missed BEFORE the pause stay put and are still skipped.
 * A run paused before `pausedAt` existed uses `updatedAt` instead: nothing else
 * writes to a paused run, so that is when it was paused.
 */
function shiftPausedRun(run: ProgramInitiation, now: Date): Pick<ProgramInitiation, 'dayStatuses' | 'startDate' | 'calculatedEndDate'> {
  const pausedOn = formatDateToLocal(run.pausedAt ?? run.updatedAt);
  const span = differenceInCalendarDays(parseISO(formatDateToLocal(now)), parseISO(pausedOn));
  const { dayStatuses, startDate, calculatedEndDate } = run;
  if (!(span > 0)) return { dayStatuses, startDate, calculatedEndDate };
  const later = (iso: string) => format(addDays(parseISO(iso), span), 'yyyy-MM-dd');
  const moves = (d?: InitiationDayStatusRecord) =>
    !!d && (d.status === 'pending' || d.status === 'rest') && d.scheduledDate >= pausedOn;
  // The run's own start and end name day 1 and the last day: they move with them.
  const first = dayStatuses.find(d => d.dayNumber === 1);
  const last = dayStatuses.reduce<InitiationDayStatusRecord | undefined>((a, d) => (!a || d.dayNumber > a.dayNumber ? d : a), undefined);
  return {
    dayStatuses: dayStatuses.map(d => (moves(d) ? { ...d, scheduledDate: later(d.scheduledDate) } : d)),
    startDate: moves(first) ? later(startDate) : startDate,
    calculatedEndDate: moves(last) ? later(calculatedEndDate) : calculatedEndDate,
  };
}

// ⚠️ Read-modify-write, NOT a wholesale upsert. The old code PUT the entire local
// {programs, enrollments, initiations, folders}, so a device holding a stale cache
// silently deleted records it had never seen (diagnosed 2026-07-27 — lib/syncBlob.ts).
// All four collections use UUIDs, so ONE tombstone map covers them.
const programSync = createBlobSync({
  transport: supabaseBlobTransport(USER_PROGRAMS_TABLE),
  owner: () => useProgramStore.getState().cachedUserId,
  onError: err => console.error('Failed to sync programs to Supabase:', err),
  build: (cloud) => {
    const { programs, enrollments, initiations, folders, deleted } = useProgramStore.getState();
    const cloudTombs = readTombstones(cloud);
    const merged = pruneTombstones(mergeTombstones(deleted, cloudTombs));
    const arr = (v: any) => (Array.isArray(v) ? v : []);
    const nextPrograms = mergeCollection(programs, arr(cloud?.programs), merged);
    const nextEnrollments = mergeCollection(enrollments, arr(cloud?.enrollments), merged);
    const nextInitiations = mergeCollection(initiations, arr(cloud?.initiations), merged);
    const nextFolders = mergeCollection(folders, arr(cloud?.folders), merged);
    if (matchesCloud([
      [nextPrograms, cloud?.programs], [nextEnrollments, cloud?.enrollments],
      [nextInitiations, cloud?.initiations], [nextFolders, cloud?.folders],
    ], merged, cloudTombs)) return null;
    return {
      programs: nextPrograms, enrollments: nextEnrollments,
      initiations: nextInitiations, folders: nextFolders, deleted: merged,
    };
  },
});

interface ProgramState {
  programs: Program[];
  enrollments: ProgramEnrollment[];
  initiations: ProgramInitiation[];
  folders: ProgramFolder[];
  userLevel: number;
  initializedUserId: string | null;
  cachedUserId: string | null;
  initialize: () => Promise<void>;

  addProgram: (program: Omit<Program, 'id' | 'createdAt' | 'updatedAt' | 'downloads' | 'averageRating' | 'reviewCount' | 'totalXP' | 'scheduleSlots'>) => string;
  updateProgram: (id: string, updates: Partial<Program>) => void;
  /** id → deletedAt across all four collections. Stops the union-on-write
   *  resurrecting anything the user deleted. */
  deleted: Tombstones;
  deleteProgram: (id: string) => void;
  duplicateProgram: (id: string) => string;

  // Folder management (flat) + archive
  addFolder: (folder: Omit<ProgramFolder, 'id' | 'createdAt' | 'updatedAt'>) => string;
  updateFolder: (id: string, updates: Partial<ProgramFolder>) => void;
  deleteFolder: (id: string) => void;
  moveProgramToFolder: (programId: string, folderId: string | undefined) => void;
  setProgramArchived: (programId: string, archived: boolean) => void;

  addModule: (programId: string, module: Omit<ProgramModule, 'id' | 'programId' | 'createdAt' | 'updatedAt'>) => string;
  updateModule: (programId: string, moduleId: string, updates: Partial<ProgramModule>) => void;
  deleteModule: (programId: string, moduleId: string) => void;
  reorderModules: (programId: string, sourceIndex: number, targetIndex: number) => void;

  addScheduleSlot: (programId: string, dayNumber: number, moduleId: string, orderInDay?: number) => void;
  removeScheduleSlot: (programId: string, dayNumber: number, moduleId: string) => void;
  updateScheduleSlots: (programId: string, slots: ScheduleSlot[]) => void;
  clearScheduleSlots: (programId: string) => void;
  getScheduleSlotsForDay: (programId: string, dayNumber: number) => ScheduleSlot[];

  initiateProgram: (programId: string, startDate: string) => string;
  pauseInitiation: (initiationId: string) => void;
  resumeInitiation: (initiationId: string) => void;
  restartProgram: (programId: string, startDate: string) => string;
  completeInitiationModule: (initiationId: string, moduleId: string, dayNumber: number, notes?: string, stepsProgress?: Record<string, boolean>) => XPDistribution | null;
  markDaySkipped: (initiationId: string, dayNumber: number) => void;
  checkAndMarkMissedDays: (initiationId: string) => void;
  getActiveInitiation: (programId: string) => ProgramInitiation | undefined;
  /** The run in flight: active, else paused. See the implementation. */
  getCurrentInitiation: (programId: string) => ProgramInitiation | undefined;
  getInitiationById: (id: string) => ProgramInitiation | undefined;
  getAllActiveInitiations: () => ProgramInitiation[];
  getTodaysInitiationModules: () => { initiation: ProgramInitiation; program: Program; modules: ProgramModule[]; dayNumber: number }[];
  getTodaysModules: (dateISO?: string) => { enrollment: { id: string; programId: string; program?: Program }; module: ProgramModule; isCompleted: boolean }[];
  generateCompletionReport: (initiationId: string) => ProgramCompletionReport | null;

  getProgramById: (id: string) => Program | undefined;
  getPublishedPrograms: () => Program[];
  getProgramsByCategory: (category: string) => Program[];
  calculateProgramXP: (modules: ProgramModule[], difficulty: ProgramDifficulty) => number;

  setUserLevel: (level: number) => void;
}

export const useProgramStore = create<ProgramState>()(
  persist(
    (set, get) => ({
      programs: [],
      folders: [],
      enrollments: [],
      initiations: [],
      userLevel: 1,
      deleted: {},
      initializedUserId: null,
      cachedUserId: null,

      // Load-on-login from the user_programs blob, keyed by user id (re-runs on
      // account switch). Cloud-wins (atomic snapshot replace) when present; else
      // keep local (or reset on a different account). Guarded so the store
      // subscription doesn't echo the load back to cloud.
      initialize: () => dedupeInit('user_programs', async () => {
        const at = accountGeneration();
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;
          if (get().initializedUserId === user.id) return;
          // Cache-first boot: while this user's local cache is fresh, skip the
          // cloud read entirely (write-through keeps cloud current meanwhile).
          if (get().cachedUserId === user.id && isCloudFresh('user_programs', user.id)) {
            set({ initializedUserId: user.id });
            return;
          }

          const { data, error } = await supabase
            .from(USER_PROGRAMS_TABLE)
            .select('*');
          // An answer for an account that has since signed out is not this copy's.
          if (at !== accountGeneration()) return;
          if (error) throw error;

          const row = (data ?? [])[0];
          set((state) => {
            const cacheIsOurs =
              state.cachedUserId === null || state.cachedUserId === user.id;
            if (row && row.data && (Array.isArray(row.data.programs) || Array.isArray(row.data.initiations))) {
              const arr = (v: any) => (Array.isArray(v) ? v : []);
              const deleted = pruneTombstones(mergeTombstones(
                cacheIsOurs ? state.deleted : {}, readTombstones(row.data)));
              const programs = mergeCollection(cacheIsOurs ? state.programs : [], arr(row.data.programs), deleted);
              const enrollments = mergeCollection(cacheIsOurs ? state.enrollments : [], arr(row.data.enrollments), deleted);
              const initiations = backfillInitiations(
                mergeCollection(cacheIsOurs ? state.initiations : [], arr(row.data.initiations), deleted));
              const folders = mergeCollection(cacheIsOurs ? state.folders : [], arr(row.data.folders), deleted);
              return {
                // Reuse the previous reference on a no-op merge so the
                // subscription doesn't fire a pointless write every boot.
                programs: sameRecords(programs, state.programs) ? state.programs : programs,
                enrollments: sameRecords(enrollments, state.enrollments) ? state.enrollments : enrollments,
                initiations: sameRecords(initiations, state.initiations) ? state.initiations : initiations,
                folders: sameRecords(folders, state.folders) ? state.folders : folders,
                deleted: sameTombstones(deleted, state.deleted) ? state.deleted : deleted,
                initializedUserId: user.id,
                cachedUserId: user.id,
              };
            }
            return cacheIsOurs
              ? { initializedUserId: user.id, cachedUserId: user.id }
              : { programs: [], enrollments: [], initiations: [], folders: [], deleted: {}, initializedUserId: user.id, cachedUserId: user.id };
          });
          programSync.writeBack(row?.data);
          markCloudFresh('user_programs', user.id);
        } catch (error) {
          console.error('Failed to initialize programs from Supabase:', error);
        }
      }),

      setUserLevel: (level) => set({ userLevel: level }),

      // ── Folder management (flat) + archive ────────────────────────────────
      addFolder: (folderData) => {
        const id = crypto.randomUUID();
        const now = new Date().toISOString();
        set((state) => ({ folders: [...state.folders, { ...folderData, id, createdAt: now, updatedAt: now }] }));
        return id;
      },
      updateFolder: (id, updates) => {
        set((state) => ({
          folders: state.folders.map((f) => (f.id === id ? { ...f, ...updates, updatedAt: new Date().toISOString() } : f)),
        }));
      },
      deleteFolder: (id) => {
        // ⚠️ Needs a tombstone — writes are a union now, so a bare filter would
        // be undone by the next merge against the cloud copy.
        set((state) => ({
          deleted: tombstone(state.deleted, id),
          folders: state.folders.filter((f) => f.id !== id),
          programs: state.programs.map((p) => (p.folderId === id ? { ...p, folderId: undefined, updatedAt: new Date().toISOString() } : p)),
        }));
      },
      moveProgramToFolder: (programId, folderId) => {
        set((state) => ({
          programs: state.programs.map((p) => (p.id === programId ? { ...p, folderId, updatedAt: new Date().toISOString() } : p)),
        }));
      },
      setProgramArchived: (programId, archived) => {
        set((state) => ({
          programs: state.programs.map((p) => (p.id === programId ? { ...p, archived, updatedAt: new Date().toISOString() } : p)),
        }));
      },

      addProgram: (programData) => {
        const id = crypto.randomUUID();
        const totalXP = get().calculateProgramXP(programData.modules || [], programData.difficulty);

        const program: Program = {
          ...programData,
          id,
          modules: programData.modules || [],
          scheduleSlots: [],
          programStatus: programData.programStatus || 'draft',
          totalXP,
          downloads: 0,
          averageRating: 0,
          reviewCount: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };

        set((state) => ({
          programs: [...state.programs, program]
        }));

        return id;
      },

      updateProgram: (id, updates) => {
        set((state) => {
          const program = state.programs.find(p => p.id === id);
          if (!program) return state;

          let totalXP = program.totalXP;
          if (updates.modules || updates.difficulty) {
            const modules = updates.modules || program.modules;
            const difficulty = updates.difficulty || program.difficulty;
            totalXP = get().calculateProgramXP(modules, difficulty);
          }

          return {
            programs: state.programs.map((p) =>
              p.id === id
                ? {
                    ...p,
                    ...updates,
                    totalXP,
                    updatedAt: new Date().toISOString()
                  }
                : p
            )
          };
        });
      },

      deleteProgram: (id) => {
        // ⚠️ Needs a tombstone — writes are a union now, so a bare filter would
        // be undone by the next merge against the cloud copy.
        // The cascade matters too: enrollments/initiations of a deleted program
        // must be tombstoned or they come back orphaned.
        set((state) => {
          let deleted = tombstone(state.deleted, id);
          for (const e of state.enrollments) if (e.programId === id) deleted = tombstone(deleted, e.id);
          for (const i of state.initiations) if (i.programId === id) deleted = tombstone(deleted, i.id);
          return {
            programs: state.programs.filter((p) => p.id !== id),
            enrollments: state.enrollments.filter((e) => e.programId !== id),
            initiations: state.initiations.filter((i) => i.programId !== id),
            deleted,
          };
        });
      },

      duplicateProgram: (id) => {
        const program = get().programs.find(p => p.id === id);
        if (!program) return '';

        const newId = crypto.randomUUID();
        const newModules = program.modules.map(module => ({
          ...module,
          id: crypto.randomUUID(),
          programId: newId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }));

        const newProgram: Program = {
          ...program,
          id: newId,
          title: `${program.title} (Copy)`,
          modules: newModules,
          isPublished: false,
          downloads: 0,
          averageRating: 0,
          reviewCount: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };

        set((state) => ({
          programs: [...state.programs, newProgram]
        }));

        return newId;
      },

      addModule: (programId, moduleData) => {
        const id = crypto.randomUUID();
        const module: ProgramModule = {
          ...moduleData,
          id,
          programId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };

        set((state) => {
          const updatedPrograms = state.programs.map((program) => {
            if (program.id !== programId) return program;

            const updatedModules = [...program.modules, module];
            const totalXP = get().calculateProgramXP(updatedModules, program.difficulty);

            return {
              ...program,
              modules: updatedModules,
              totalXP,
              updatedAt: new Date().toISOString()
            };
          });

          return { programs: updatedPrograms };
        });

        return id;
      },

      updateModule: (programId, moduleId, updates) => {
        set((state) => {
          const updatedPrograms = state.programs.map((program) => {
            if (program.id !== programId) return program;

            const updatedModules = program.modules.map((module) =>
              module.id === moduleId
                ? { ...module, ...updates, updatedAt: new Date().toISOString() }
                : module
            );

            const totalXP = get().calculateProgramXP(updatedModules, program.difficulty);

            return {
              ...program,
              modules: updatedModules,
              totalXP,
              updatedAt: new Date().toISOString()
            };
          });

          return { programs: updatedPrograms };
        });
      },

      deleteModule: (programId, moduleId) => {
        set((state) => {
          const updatedPrograms = state.programs.map((program) => {
            if (program.id !== programId) return program;

            const updatedModules = program.modules
              .filter((module) => module.id !== moduleId)
              .map((module, index) => ({ ...module, order: index }));

            const totalXP = get().calculateProgramXP(updatedModules, program.difficulty);

            return {
              ...program,
              modules: updatedModules,
              totalXP,
              updatedAt: new Date().toISOString()
            };
          });

          return { programs: updatedPrograms };
        });
      },

      reorderModules: (programId, sourceIndex, targetIndex) => {
        set((state) => {
          const program = state.programs.find(p => p.id === programId);
          if (!program) return state;

          const modules = [...program.modules];
          const [removed] = modules.splice(sourceIndex, 1);
          modules.splice(targetIndex, 0, removed);

          const updatedModules = modules.map((module, index) => ({
            ...module,
            order: index
          }));

          return {
            programs: state.programs.map((p) =>
              p.id === programId
                ? { ...p, modules: updatedModules, updatedAt: new Date().toISOString() }
                : p
            )
          };
        });
      },

      addScheduleSlot: (programId, dayNumber, moduleId, orderInDay = 0) => {
        set((state) => {
          const program = state.programs.find(p => p.id === programId);
          if (!program) return state;

          const existingSlot = program.scheduleSlots.find(
            s => s.dayNumber === dayNumber && s.moduleId === moduleId
          );
          if (existingSlot) return state;

          const newSlot: ScheduleSlot = {
            id: crypto.randomUUID(),
            programId,
            dayNumber,
            moduleId,
            orderInDay,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };

          return {
            programs: state.programs.map(p =>
              p.id === programId
                ? {
                    ...p,
                    scheduleSlots: [...p.scheduleSlots, newSlot].sort((a, b) =>
                      a.dayNumber - b.dayNumber || a.orderInDay - b.orderInDay
                    ),
                    updatedAt: new Date().toISOString()
                  }
                : p
            )
          };
        });
      },

      removeScheduleSlot: (programId, dayNumber, moduleId) => {
        set((state) => ({
          programs: state.programs.map(p =>
            p.id === programId
              ? {
                  ...p,
                  scheduleSlots: p.scheduleSlots.filter(
                    s => !(s.dayNumber === dayNumber && s.moduleId === moduleId)
                  ),
                  updatedAt: new Date().toISOString()
                }
              : p
          )
        }));
      },

      updateScheduleSlots: (programId, slots) => {
        set((state) => ({
          programs: state.programs.map(p =>
            p.id === programId
              ? {
                  ...p,
                  scheduleSlots: slots.sort((a, b) =>
                    a.dayNumber - b.dayNumber || a.orderInDay - b.orderInDay
                  ),
                  updatedAt: new Date().toISOString()
                }
              : p
          )
        }));
      },

      clearScheduleSlots: (programId) => {
        set((state) => ({
          programs: state.programs.map(p =>
            p.id === programId
              ? { ...p, scheduleSlots: [], updatedAt: new Date().toISOString() }
              : p
          )
        }));
      },

      getScheduleSlotsForDay: (programId, dayNumber) => {
        const program = get().programs.find(p => p.id === programId);
        if (!program) return [];
        return program.scheduleSlots
          .filter(s => s.dayNumber === dayNumber)
          .sort((a, b) => a.orderInDay - b.orderInDay);
      },

      initiateProgram: (programId, startDate) => {
        const program = get().programs.find(p => p.id === programId);
        if (!program) return '';

        // ⚠️ A PAUSED run blocks a second start too. This used to check only `active`,
        // so starting a program whose run was paused left the paused run stranded
        // beside a new one. Starting again REOPENS the run in flight (owner's choice,
        // 2026-10-16); a fresh start is Restart on the run screen — it asks first, and
        // restartProgram closes the paused run.
        const existing = get().getCurrentInitiation(programId);
        if (existing) return existing.id;

        const id = crypto.randomUUID();
        const startDateObj = parseISO(startDate);
        const calculatedEndDate = format(addDays(startDateObj, program.durationDays - 1), 'yyyy-MM-dd');

        const dayStatuses: InitiationDayStatusRecord[] = [];
        for (let day = 1; day <= program.durationDays; day++) {
          const scheduledDate = format(addDays(startDateObj, day - 1), 'yyyy-MM-dd');
          const hasModules = program.scheduleSlots.some(s => s.dayNumber === day);

          dayStatuses.push({
            id: crypto.randomUUID(),
            initiationId: id,
            dayNumber: day,
            scheduledDate,
            status: hasModules ? 'pending' : 'rest',
            createdAt: new Date().toISOString()
          });
        }

        const initiation: ProgramInitiation = {
          id,
          programId,
          userId: 'local-user',
          startDate,
          calculatedEndDate,
          currentDay: 1,
          status: 'active',
          totalXPEarned: 0,
          moduleXPEarned: 0,
          modulesCompleted: 0,
          modulesSkipped: 0,
          completionPercentage: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          dayStatuses,
          completedModuleIds: []
        };

        set((state) => ({
          initiations: [...state.initiations, initiation],
          programs: state.programs.map(p =>
            p.id === programId
              ? { ...p, programStatus: 'active' as ProgramStatus, updatedAt: new Date().toISOString() }
              : p
          )
        }));

        return id;
      },

      pauseInitiation: (initiationId) => {
        const now = new Date().toISOString();
        // Pausing a paused run again must not move its pause date (Resume measures from it).
        set((state) => ({
          initiations: state.initiations.map(i =>
            i.id === initiationId && i.status !== 'paused'
              ? { ...i, status: 'paused' as const, pausedAt: now, updatedAt: now }
              : i
          ),
          programs: state.programs.map(p => {
            const init = state.initiations.find(i => i.id === initiationId);
            if (init && p.id === init.programId) {
              return { ...p, programStatus: 'paused' as ProgramStatus, updatedAt: new Date().toISOString() };
            }
            return p;
          })
        }));
      },

      resumeInitiation: (initiationId) => {
        const now = new Date();
        // Only a PAUSED run moves: a second Resume on a run already running would
        // read its last change as a pause and push its days on again.
        set((state) => ({
          initiations: state.initiations.map(i =>
            i.id === initiationId && i.status === 'paused'
              ? { ...i, ...shiftPausedRun(i, now), status: 'active' as const, pausedAt: undefined, updatedAt: now.toISOString() }
              : i
          ),
          programs: state.programs.map(p => {
            const init = state.initiations.find(i => i.id === initiationId);
            if (init && p.id === init.programId) {
              return { ...p, programStatus: 'active' as ProgramStatus, updatedAt: new Date().toISOString() };
            }
            return p;
          })
        }));
      },

      restartProgram: (programId, startDate) => {
        // ⚠️ Closes a PAUSED run as well — Restart shows on a paused run, and closing
        // only `active` ones left the paused run open beside the new one.
        // ⚠️ The close must bump `updatedAt`: the blob merge gives a tie to the cloud,
        // which already holds the run as it was, so an unstamped close came back
        // paused/active beside the new run on the next sync (2026-10-16).
        const existing = get().getCurrentInitiation(programId);
        if (existing) {
          const now = new Date().toISOString();
          set((state) => ({
            initiations: state.initiations.map(i =>
              i.id === existing.id
                ? { ...i, status: 'partially_completed' as const, completedAt: now, updatedAt: now }
                : i
            )
          }));
        }

        return get().initiateProgram(programId, startDate);
      },

      completeInitiationModule: (initiationId, moduleId, dayNumber, notes, stepsProgress) => {
        const initiation = get().initiations.find(i => i.id === initiationId);
        if (!initiation) return null;

        const program = get().programs.find(p => p.id === initiation.programId);
        if (!program) return null;

        const module = program.modules.find(m => m.id === moduleId);
        if (!module) return null;

        // ⚠️ Once per module per day — finishing it again must not pay its XP twice.
        const doneToday = initiation.dayStatuses.find(ds => ds.dayNumber === dayNumber)?.completedModuleIds ?? [];
        if (doneToday.includes(moduleId)) return null;

        const userLevel = get().userLevel;
        const scaledModuleXP = calculateScaledModuleXP(userLevel, program.difficulty);

        const baseXpDistribution = calculateModuleXPFromModule(module);
        baseXpDistribution.totalXP = scaledModuleXP;
        baseXpDistribution.domainXP = scaledModuleXP;
        baseXpDistribution.userXP = scaledModuleXP;

        if (module.performanceParameters.length > 0) {
          const xpPerParameter = scaledModuleXP / module.performanceParameters.length;
          module.performanceParameters.forEach(param => {
            baseXpDistribution.performanceParameterXP[param] = Math.round(xpPerParameter * 100) / 100;
          });
        }

        const today = formatDateToLocal(new Date());
        const habitXPRecords: HabitXPRecord[] = [];
        let habitTotalXP = 0;

        if (module.linkedHabitIds && module.linkedHabitIds.length > 0) {
          const habitStore = useHabitStore.getState();

          module.linkedHabitIds.forEach(habitId => {
            const habit = habitStore.habits.find(h => h.id === habitId);
            if (!habit || habit.archivedAt) return;

            const wasAlreadyCompleted = habit.completedDates.includes(today);
            const baseHabitXP = POINTS.HABIT || 25;
            const xpMultiplier = wasAlreadyCompleted ? 0.5 : 1.0;
            const earnedXP = Math.round(baseHabitXP * xpMultiplier);

            if (!wasAlreadyCompleted) {
              habitStore.toggleHabit(habitId, today);
            }

            habitXPRecords.push({
              habitId: habit.id,
              habitName: habit.name,
              xp: earnedXP,
              wasAlreadyCompleted
            });
            habitTotalXP += earnedXP;
          });
        }

        const xpDistribution: XPDistribution = {
          ...baseXpDistribution,
          habitXP: habitXPRecords,
          domainXP: scaledModuleXP + habitTotalXP,
          userXP: scaledModuleXP + habitTotalXP,
          totalXP: scaledModuleXP + habitTotalXP
        };

        Object.entries(xpDistribution.performanceParameterXP).forEach(([param, xp]) => {
          useFitnessStore.getState().addXP(param, xp, `Program: ${program.title} - ${module.title}`);
        });

        Object.entries(xpDistribution.muscleGroupXP).forEach(([group, xp]) => {
          useFitnessStore.getState().addXP(group, xp, `Program: ${program.title} - ${module.title}`);
        });

        const totalScheduledModules = program.scheduleSlots.length;
        const newModulesCompleted = initiation.modulesCompleted + 1;
        const newTotalXP = initiation.totalXPEarned + xpDistribution.totalXP;
        const newModuleXP = (initiation.moduleXPEarned || 0) + baseXpDistribution.totalXP;
        const newCompletionPercentage = totalScheduledModules > 0
          ? Math.round((newModulesCompleted / totalScheduledModules) * 100)
          : 0;

        let programJustCompleted = false;
        let completionBonusXP = 0;
        let durationBonusXP = 0;

        set((state) => ({
          initiations: state.initiations.map(i => {
            if (i.id !== initiationId) return i;

            // ⚠️⚠️ The day is COMPLETE only once every module on it is (2026-10-16): a
            // two-module day used to complete — and leave today's card — on the first.
            const dayModuleIds = [...new Set(program.scheduleSlots.filter(s => s.dayNumber === dayNumber).map(s => s.moduleId))];
            const updatedDayStatuses = i.dayStatuses.map(ds => {
              if (ds.dayNumber !== dayNumber) return ds;
              const completedModuleIds = [...(ds.completedModuleIds ?? []), moduleId];
              const dayDone = dayModuleIds.every(id => completedModuleIds.includes(id));
              return dayDone
                ? { ...ds, completedModuleIds, status: 'completed' as const, completedAt: new Date().toISOString() }
                : { ...ds, completedModuleIds };
            });

            const allDaysProcessed = updatedDayStatuses.every(
              ds => ds.status === 'completed' || ds.status === 'skipped' || ds.status === 'rest'
            );

            const completionStatus = allDaysProcessed
              ? calculateCompletionStatus(newCompletionPercentage)
              : i.status;

            if (allDaysProcessed && !i.completedAt) {
              programJustCompleted = true;
              completionBonusXP = calculateScaledCompletionBonus(userLevel, program.difficulty);
              durationBonusXP = calculateDurationBonus(program.durationDays, newCompletionPercentage, userLevel);
            }

            const totalBonusXP = completionBonusXP + durationBonusXP;

            return {
              ...i,
              modulesCompleted: newModulesCompleted,
              completedModuleIds: (i.completedModuleIds ?? []).includes(moduleId)
                ? (i.completedModuleIds ?? [])
                : [...(i.completedModuleIds ?? []), moduleId],
              totalXPEarned: newTotalXP + (allDaysProcessed && !i.completedAt ? totalBonusXP : 0),
              moduleXPEarned: newModuleXP + (allDaysProcessed && !i.completedAt ? totalBonusXP : 0),
              completionPercentage: newCompletionPercentage,
              status: completionStatus,
              completedAt: allDaysProcessed ? new Date().toISOString() : undefined,
              dayStatuses: updatedDayStatuses,
              updatedAt: new Date().toISOString()
            };
          })
        }));

        useActivityLogStore.getState().addActivity({
          activityType: 'program_module' as any,
          activityName: `${program.title} - ${module.title}`,
          domainId: program.category,
          xpEarned: xpDistribution.totalXP,
          metadata: {
            programId: program.id,
            programName: program.title,
            moduleId: module.id,
            moduleName: module.title,
            dayNumber,
            xpDistribution,
            habitXP: habitXPRecords
          },
          completedAt: new Date().toISOString()
        });

        if (programJustCompleted) {
          const totalBonusXP = completionBonusXP + durationBonusXP;
          useActivityLogStore.getState().addActivity({
            activityType: 'program_completion' as any,
            activityName: `${program.title} - Program Completed`,
            domainId: program.category,
            xpEarned: totalBonusXP,
            metadata: {
              programId: program.id,
              programName: program.title,
              completionPercentage: newCompletionPercentage,
              totalModulesCompleted: newModulesCompleted,
              totalXPEarned: newTotalXP + totalBonusXP,
              completionBonusXP,
              durationBonusXP,
              userLevel,
              difficulty: program.difficulty
            },
            completedAt: new Date().toISOString()
          });
        }

        return xpDistribution;
      },

      markDaySkipped: (initiationId, dayNumber) => {
        set((state) => ({
          initiations: state.initiations.map(i => {
            if (i.id !== initiationId) return i;

            const dayStatus = i.dayStatuses.find(ds => ds.dayNumber === dayNumber);
            if (!dayStatus || dayStatus.status !== 'pending') return i;

            const updatedDayStatuses = i.dayStatuses.map(ds =>
              ds.dayNumber === dayNumber
                ? { ...ds, status: 'skipped' as const, skippedAt: new Date().toISOString() }
                : ds
            );

            return {
              ...i,
              modulesSkipped: i.modulesSkipped + 1,
              dayStatuses: updatedDayStatuses,
              updatedAt: new Date().toISOString()
            };
          })
        }));
      },

      checkAndMarkMissedDays: (initiationId) => {
        const initiation = get().initiations.find(i => i.id === initiationId);
        if (!initiation || initiation.status !== 'active') return;

        const today = formatDateToLocal(new Date());

        initiation.dayStatuses.forEach(dayStatus => {
          if (dayStatus.status === 'pending' && dayStatus.scheduledDate < today) {
            get().markDaySkipped(initiationId, dayStatus.dayNumber);
          }
        });
      },

      getActiveInitiation: (programId) => {
        return get().initiations.find(
          i => i.programId === programId && i.status === 'active'
        );
      },

      /**
       * ⚠️⚠️ **A PAUSED RUN IS STILL THE PROGRAM'S RUN.** `getActiveInitiation` asks
       * "is one running right now?", and a paused run is not. But the Programs hub
       * used it to decide Continue vs Start, so a paused run vanished from the hub
       * the moment you left its screen and its Resume button could never be reached
       * again (owner, 2026-10-16). This is the run in flight — the one to reopen,
       * and the one that blocks a second start.
       * The active run wins; with only paused ones (older data can hold two), the
       * newest.
       */
      getCurrentInitiation: (programId) => {
        const runs = get().initiations.filter(i => i.programId === programId);
        return runs.find(i => i.status === 'active')
          ?? runs.filter(i => i.status === 'paused').sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
      },

      getInitiationById: (id) => {
        return get().initiations.find(i => i.id === id);
      },

      getAllActiveInitiations: () => {
        return get().initiations.filter(i => i.status === 'active');
      },

      getTodaysInitiationModules: () => {
        const today = formatDateToLocal(new Date());
        const activeInitiations = get().getAllActiveInitiations();
        const result: { initiation: ProgramInitiation; program: Program; modules: ProgramModule[]; dayNumber: number }[] = [];

        activeInitiations.forEach(initiation => {
          const dayStatus = initiation.dayStatuses.find(ds => ds.scheduledDate === today);
          if (!dayStatus || dayStatus.status !== 'pending') return;

          const program = get().programs.find(p => p.id === initiation.programId);
          if (!program) return;

          const daySlots = program.scheduleSlots.filter(s => s.dayNumber === dayStatus.dayNumber);
          const modules = daySlots
            .map(slot => program.modules.find(m => m.id === slot.moduleId))
            .filter((m): m is ProgramModule => m !== undefined);

          if (modules.length > 0) {
            result.push({
              initiation,
              program,
              modules,
              dayNumber: dayStatus.dayNumber
            });
          }
        });

        return result;
      },

      // Optional local date for Home browsing; existing no-argument callers stay on today.
      getTodaysModules: (dateISO = formatDateToLocal(new Date())) => {
        const activeInitiations = get().getAllActiveInitiations();
        const result: { enrollment: { id: string; programId: string; program?: Program }; module: ProgramModule; isCompleted: boolean }[] = [];

        activeInitiations.forEach(initiation => {
          const dayStatus = initiation.dayStatuses.find(ds => ds.scheduledDate === dateISO);
          if (!dayStatus) return;

          const program = get().programs.find(p => p.id === initiation.programId);
          if (!program) return;

          const daySlots = program.scheduleSlots.filter(s => s.dayNumber === dayStatus.dayNumber);
          const modules = daySlots
            .map(slot => program.modules.find(m => m.id === slot.moduleId))
            .filter((m): m is ProgramModule => m !== undefined);

          modules.forEach(module => {
            result.push({
              enrollment: {
                id: initiation.id,
                programId: initiation.programId,
                program
              },
              module,
              // Each module on its own — a two-module day is half done after one.
              isCompleted: dayStatus.status === 'completed' || !!dayStatus.completedModuleIds?.includes(module.id)
            });
          });
        });

        return result;
      },

      generateCompletionReport: (initiationId) => {
        const initiation = get().initiations.find(i => i.id === initiationId);
        if (!initiation) return null;

        const program = get().programs.find(p => p.id === initiation.programId);
        if (!program) return null;

        const completedDays = initiation.dayStatuses.filter(ds => ds.status === 'completed');
        const skippedDays = initiation.dayStatuses.filter(ds => ds.status === 'skipped');

        const totalXPPossible = calculateProgramTotalXP(program.modules);

        return {
          initiationId,
          programId: program.id,
          programTitle: program.title,
          startDate: initiation.startDate,
          endDate: initiation.completedAt || initiation.calculatedEndDate,
          totalDays: program.durationDays,
          daysCompleted: completedDays.length,
          daysSkipped: skippedDays.length,
          totalXPEarned: initiation.totalXPEarned,
          totalXPPossible,
          completionPercentage: initiation.completionPercentage,
          status: initiation.status as 'completed' | 'partially_completed',
          performanceParameterXP: {},
          muscleGroupXP: {},
          moduleCompletions: [],
          skippedDays: skippedDays.map(ds => ds.dayNumber),
          linkedHabitsCompleted: [],
          totalDomainXP: initiation.totalXPEarned,
          totalUserXP: initiation.totalXPEarned
        };
      },

      getProgramById: (id) => {
        return get().programs.find(p => p.id === id);
      },

      getPublishedPrograms: () => {
        return get().programs.filter(p => p.isPublished);
      },

      getProgramsByCategory: (category) => {
        return get().programs.filter(p => p.category === category);
      },

      calculateProgramXP: (modules, difficulty) => {
        const userLevel = get().userLevel;
        const moduleXP = calculateScaledModuleXP(userLevel, difficulty);
        const modulesXP = modules.length * moduleXP;
        const completionBonus = calculateScaledCompletionBonus(userLevel, difficulty);
        return modulesXP + completionBonus;
      }
    }),
    {
      name: 'programs-storage',
      // ⭐ One saved copy per account — see `scopeToAccount` below.
      storage: accountStorage(cachedUserIdOwner),
      version: 1,
      // initializedUserId stays transient so load-on-login runs each start.
      partialize: (state: ProgramState) => ({
        programs: state.programs,
        enrollments: state.enrollments,
        initiations: state.initiations,
        folders: state.folders,
        // Must persist: a tombstone that dies on reload lets the next merge
        // resurrect the record it was suppressing.
        deleted: state.deleted,
        userLevel: state.userLevel,
        cachedUserId: state.cachedUserId,
      }) as any,
    }
  )
);

// Auto-sync to Supabase whenever programs/enrollments/initiations change.
// Ref-equality skips non-data changes (e.g. userLevel). No suppression flag:
// initialize() reuses references on a no-op merge, and a write-back when it DID
// change something is what makes the two sides converge.
useProgramStore.subscribe((state, prev) => {
  if (swappingAccount()) return; // a change of account swaps the saved copy in — not an edit
  if (
    state.programs === prev.programs &&
    state.enrollments === prev.enrollments &&
    state.initiations === prev.initiations &&
    state.folders === prev.folders &&
    state.deleted === prev.deleted
  ) return;
  programSync.schedule();
});

/* ⚠️⚠️ ONE SAVED COPY PER ACCOUNT (lib/accountStorage). The device kept one copy, tagged with the
   account it was loaded for, and a sign-out does not reload the page: an account's edits whose
   write had not landed lived only until the next account's load replaced the copy, and the next
   account's edits made before its own load were merged into the last one's row by ITS next load. */
scopeToAccount(useProgramStore);
